import PropTypes from 'prop-types';
// @mui
import { Stack, CardContent, Box, useTheme, Dialog } from '@mui/material';
import Card from '@mui/material/Card';
import CloseIcon from '@mui/icons-material/Close';
import { CSSObject, Theme } from '@mui/material/styles';

interface Props {
  children?: PropTypes.ReactElementLike | PropTypes.ReactElementLike[];
  styleObj?: any;
  handleClick?: any;
  style?: CSSObject;
  open?: boolean;
  handleClose?: any;
  className?: any;
}

export const formStyleMixin = (theme: Theme): CSSObject => ({
  borderRadius: '8px',
  boxShadow: '0 4px 12px 0 rgba(44, 62, 80, 0.16)',
  border: 'solid 1px ' + theme.palette.root.darkGrayed,
  [theme.breakpoints.down('sm')]: {
    border: 'none',
    boxShadow: 'none',
    '& .MuiCardContent-root': {
      padding: '0!important',
    },
  },
});

export const InfoCard = (props: Props) => {
  const theme = useTheme();
  return (
    <Card sx={{ ...props.style }}>
      <CardContent
        sx={{
          padding: '32px',
          ...formStyleMixin(theme),
          [theme.breakpoints.down('sm')]: {
            padding: '16px',
          },
        }}
      >
        <Stack spacing={3}>{props.children}</Stack>
      </CardContent>
    </Card>
  );
};

export const DataCard = (props: Props) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        ...props.style,
        [theme.breakpoints.down('sm')]: {
          boxShadow: 'none',
          '& .MuiCardContent-root': {
            boxShadow: 'none',
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      }}
    >
      <CardContent
        sx={{
          ...formStyleMixin(theme),
          height: '100%',
          '& .MuiTableCell-root': {
            padding: '8px 16px',
            pl: '8px',
            '&:last-child': {
              [theme.breakpoints.up('sm')]: {
                pr: 0,
              },
            },
            '&:first-of-type': {
              color: theme.palette.root.basicDark_60,
              '& .MuiTypography-body1': {
                fontSize: theme.typography.semiCaptionRegular.fontSize,
              },
            },
            [theme.breakpoints.down('sm')]: {
              padding: '8px',
            },
          },
        }}
      >
        <Stack spacing={1}>{props.children}</Stack>
      </CardContent>
    </Card>
  );
};

export const MessageCard = (props: Props) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        padding: '48px',
        ...formStyleMixin(theme),
        [theme.breakpoints.down('sm')]: {
          padding: '16px',
        },
      }}
    >
      <CardContent>
        <Stack spacing={3}>{props.children}</Stack>
      </CardContent>
    </Card>
  );
};

export const PageHeaderCard = (props: Props) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        background: theme.palette.root.darkGrayed_024,
        margin: '-24px',
        borderRadius: 0,
        boxShadow: 'none',
      }}
    >
      <CardContent>{props.children}</CardContent>
    </Card>
  );
};

export const DetailsColumnCard2 = (props: Props) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        padding: '0px 0px 0px 24px',
        borderLeft: '1px solid #cbdcde',
        [theme.breakpoints.down('sm')]: {
          padding: 0,
          borderLeft: 0,
        },
      }}
    >
      {props.children}
    </Box>
  );
};

export const DetailsColumnCard = (props: Props) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        padding: '24px 0px 24px 24px',
        borderLeft: '1px solid #cbdcde',
        [theme.breakpoints.down('sm')]: {
          padding: 0,
          borderLeft: 0,
        },
      }}
    >
      {props.children}
    </Box>
  );
};

export const LightGreenCard = (props: Props) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        padding: '16px',
        [theme.breakpoints.up('sm')]: {
          marginLeft: '-16px!important',
          marginRight: '-16px!important',
        },
        backgroundColor: theme.palette.root.darkGrayed_024,
        borderRadius: '8px',
      }}
    >
      {props.children}
    </Box>
  );
};

export const BorderCard = (props: Props) => {
  const theme = useTheme();
  return (
    <Box
      className={props.className || ''}
      onClick={props.handleClick}
      sx={{
        borderRadius: '8px',
        display: 'flex',
        border: `1px solid`,
        ...props.styleObj,
        '&.favorite': {
          background: theme.palette.root.favoritePlan,
          '& .planDescription': {
            color: '#fff',
            fontFamily: 'RubikMedium!important',
          },
          '& .charge': {
            color: '#fff',
            fontFamily: 'RubikMedium!important',
          }
        }
      }}
    >
      {props.children}
    </Box>
  );
};

export const StepperCard = (props: Props) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        padding: '7px',
        [theme.breakpoints.up('sm')]: {
          marginLeft: '-7px!important',
          marginRight: '-7px!important',
        },
        backgroundColor: theme.palette.root.brandSecondary_012,
        borderRadius: '8px',
        ...props?.style,
      }}
    >
      {props.children}
    </Box>
  );
};

export const DialogCard = (props: Props) => {
  const theme = useTheme();
  const dstyle = props.style || {};
  return (
    <Dialog
      open={props.open || false}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{ '& .MuiPaper-root': { margin: '16px', ...dstyle } }}
    >
      <Box sx={{ padding: '12px' }}>
        <Stack spacing={0} overflow={'hidden'}>
          <Box sx={{ height: '40px' }}>
            <Box
              sx={{
                background: theme.palette.root.darkGrayed_028,
                width: '40px',
                height: '40px',
                textAlign: 'center',
                float: 'right',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              <CloseIcon
                onClick={props.handleClose}
                sx={{ width: '20px', height: '40px', margin: 'auto' }}
              />
            </Box>
          </Box>
          {props.children}
        </Stack>
      </Box>
    </Dialog>
  );
};
